import * as React from 'react';
import styled from 'styled-components';

import DocsHelper from 'util/DocsHelper';
import useCurrentUser from 'hooks/useCurrentUser';
import { isPermitted } from 'util/PermissionsMixin';
import { PageHeader, Icon } from 'components/common';
import { Alert, Col, Button } from 'components/bootstrap';
import { useModalContext } from 'common/contexts';

import ProceduresList from './List';
import StepsList from './EventProcedureSteps/StepsList';

const StyledIndexRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  gap: 0.5rem;
`;

const RoundedAlert = styled(Alert)`
  border-radius: 4px;
  padding: ${({ theme }) => `${theme.spacings.sm} ${theme.spacings.md}`};
  width: 100%;
  margin: 1.04rem 0 0.37rem 0;

  & .mantine-Alert-wrapper {
    flex-direction: row;
    align-items: center;
    width: 100%;
  }
`;

const StyledIndexWrapper = styled.div`
  flex: 1;
  border: 1px solid ${(props: any) => props.theme.colors.variant.lighter.default};
  border-radius: 5px;
  padding: 5px 10px;
  background-color: ${(props: any) => props.theme.colors.global.contentBackground};
`;

type Props = {
  viewType: string;
  useCoreRoutes?: boolean;
};

function SecurityUI({ viewType, useCoreRoutes = false }: Props) {
  const { setModal } = useModalContext();

  const openEventProcedureModal = () => {
    setModal('CREATE-EVENT-PROCEDURE');
  };

  const openEventProcedureStepModal = () => {
    setModal('CREATE-EVENT-PROCEDURE-STEP');
  };

  const { permissions } = useCurrentUser();
  const canCreateEventProcedures = React.useMemo(
    () => isPermitted(permissions, 'event_procedures:create'),
    [permissions],
  );
  const canCreateEventProcedureSteps = React.useMemo(
    () => isPermitted(permissions, 'event_procedure_steps:create'),
    [permissions],
  );

  return (
    <>
      <Col md={12}>
        {viewType === 'procedures' ? (
          <PageHeader
            title="Event Procedures"
            documentationLink={{
              title: 'Event Procedures Documentation',
              path: DocsHelper.PAGES.EVENT_PROCEDURES,
            }}
            actions={
              canCreateEventProcedures && (
                <Button bsStyle="primary" onClick={() => openEventProcedureModal()}>
                  <Icon name="add" size="xs" />
                  Create Event Procedure
                </Button>
            )} />
        ) : (
          <PageHeader
            title="Event Procedure Steps"
            actions={
              canCreateEventProcedureSteps && (
                <Button bsStyle="primary" onClick={() => openEventProcedureStepModal()}>
                  <Icon name="add" size="xs" />
                  Create Event Procedure Step
                </Button>
            )}
          />
        )}
      </Col>
      <RoundedAlert bsStyle="info">
        <span>
          We&apos;re excited to share this Early Access feature with you, and look forward to hearing how it supports your workflow. As we continue to improve the experience, please refer to our documentation for additional guidance and details.
        </span>
      </RoundedAlert>
      <StyledIndexRow>
        <StyledIndexWrapper>
          {viewType === 'procedures' ? (
            <ProceduresList useCoreRoutes={useCoreRoutes} />
          ) : (
            <StepsList useCoreRoutes={useCoreRoutes} />
          )}
        </StyledIndexWrapper>
      </StyledIndexRow>
    </>
  );
}

export default SecurityUI;
